import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import LossReasonsAPI from '../../api/lossReasons';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getLossReasons(_state) {
    return _state.records;
  },
  getActiveLossReasons(_state) {
    return _state.records
      .filter(record => record.is_active)
      .sort((a, b) => a.position - b.position);
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getLossReasonById: _state => id => {
    return _state.records.find(record => record.id === Number(id));
  },
};

export const actions = {
  get: async function getLossReasons({ commit }, activeOnly = false) {
    commit(types.SET_LOSS_REASON_UI_FLAG, { isFetching: true });
    try {
      const response = await LossReasonsAPI.getAll(activeOnly);
      const sortedReasons = response.data.payload.sort(
        (a, b) => a.position - b.position
      );
      commit(types.SET_LOSS_REASONS, sortedReasons);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_LOSS_REASON_UI_FLAG, { isFetching: false });
    }
  },

  create: async function createLossReason({ commit }, lossReasonData) {
    commit(types.SET_LOSS_REASON_UI_FLAG, { isCreating: true });
    try {
      const response = await LossReasonsAPI.create(lossReasonData);
      commit(types.ADD_LOSS_REASON, response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_LOSS_REASON_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updateLossReason({ commit }, { id, ...updateObj }) {
    commit(types.SET_LOSS_REASON_UI_FLAG, { isUpdating: true });
    try {
      const response = await LossReasonsAPI.update(id, updateObj);
      commit(types.EDIT_LOSS_REASON, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LOSS_REASON_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteLossReason({ commit }, id) {
    commit(types.SET_LOSS_REASON_UI_FLAG, { isDeleting: true });
    try {
      await LossReasonsAPI.delete(id);
      commit(types.DELETE_LOSS_REASON, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_LOSS_REASON_UI_FLAG, { isDeleting: false });
    }
  },

  reorder: async function reorderLossReasons(
    { commit, state: _state },
    positions
  ) {
    try {
      await LossReasonsAPI.reorder(positions);
      // Update local state with new positions
      const updatedRecords = _state.records.map(record => {
        const positionData = positions.find(p => p.id === record.id);
        if (positionData) {
          return { ...record, position: positionData.position };
        }
        return record;
      });
      commit(
        types.SET_LOSS_REASONS,
        updatedRecords.sort((a, b) => a.position - b.position)
      );
    } catch (error) {
      throw new Error(error);
    }
  },
};

export const mutations = {
  [types.SET_LOSS_REASON_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_LOSS_REASONS]: MutationHelpers.set,
  [types.ADD_LOSS_REASON]: MutationHelpers.create,
  [types.EDIT_LOSS_REASON]: MutationHelpers.update,
  [types.DELETE_LOSS_REASON]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
